import {
  BarChart3,
  Calendar,
  CheckCircle,
  Frown,
  Meh,
  MessageSquare,
  Smile,
  TrendingUp,
} from "lucide-react";
import React, { useCallback, useState } from "react";
import { SectionHeader } from "../../components";
import analyticsService from "../../services/analyticsService";
import { SentimentData } from "../../types/analytics";
import { cn } from "../../utils/cn";
import Button from "../ui/Button";
import Card from "../ui/Card";
import Textarea from "../ui/Textarea";
import SentimentAnalysisSkeleton from "./skeletons/SentimentAnalysisSkeleton";

const SentimentAnalysis: React.FC = () => {
  const [sentimentData, setSentimentData] = useState<SentimentData | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [commentsInput, setCommentsInput] = useState<string>("");
  const [postId, setPostId] = useState<string>("");

  const fetchSentimentData = useCallback(async () => {
    const comments = commentsInput
      .split("\n")
      .map((c) => c.trim())
      .filter((c) => c.length > 0);

    if (comments.length === 0) {
      setError("Forneça ao menos um comentário para análise.");
      setSentimentData(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = await analyticsService.getSentimentAnalysis(
        comments,
        postId ? Number(postId) : undefined,
      );
      if (data.success) {
        const analysis = data.analysis || {};
        const sentimentDataInstance: SentimentData = {
          sentiment_distribution: {
            positive: analysis.positive_posts || 0,
            neutral: analysis.neutral_posts || 0,
            negative: analysis.negative_posts || 0,
          },
          post_sentiments: [],
          insights: [
            {
              type: "info",
              message: `Vetor predominante: ${analysis.overall_sentiment || "Neutro"}`,
              recommendation:
                "Afinidade identificada com o perfil comportamental da audiência.",
            },
          ],
          total_analyzed:
            (analysis.positive_posts || 0) +
            (analysis.neutral_posts || 0) +
            (analysis.negative_posts || 0),
        };
        setSentimentData(sentimentDataInstance);
      } else {
        throw new Error("Erro ao obter análise de sentimento");
      }
    } catch (err: any) {
      const message = err?.message || "";
      if (
        message.includes("404") ||
        message.includes("Not Found") ||
        message.includes("503")
      ) {
        setError(
          "ML Engine indisponível para este nó. Exibindo projeção de demonstração.",
        );
        setSentimentData({
          sentiment_distribution: { positive: 65, neutral: 25, negative: 10 },
          post_sentiments: [],
          insights: [
            {
              type: "info",
              message: "Sentimento Geral: Exponencialmente Positivo",
              recommendation:
                "Manter a cadência de posts com alta carga emocional positiva.",
            },
          ],
          total_analyzed: 100,
        });
      } else {
        setError(
          err instanceof Error
            ? err.message
            : "Erro na consulta de sentimentos.",
        );
      }
    } finally {
      setLoading(false);
    }
  }, [commentsInput, postId]);

  if (loading) {
    return <SentimentAnalysisSkeleton />;
  }

  return (
    <div className="space-y-2 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Header section with demonstration alert — mesmo tratamento visual
          do header de "Perfil Estratégico do ICP" (EmpathyMapDisplay.tsx):
          SectionHeader real dentro do seu próprio card. */}
      <div className="bg-white border border-purple-200 rounded-xl p-3 shadow-xs">
        <SectionHeader
          icon={<MessageSquare className="h-5 w-5" />}
          title="Pulso da Audiência"
          subtitle={
            sentimentData
              ? `Analisando ${sentimentData.total_analyzed} interações via IA`
              : "Aguardando entrada de comentários"
          }
          hideBorder
          action={
            error &&
            (error.includes("demonstração") ||
              error.includes("indisponível")) ? (
              <div className="px-3 py-1.5 bg-amber-50 border border-amber-100 rounded-lg flex items-center gap-2 self-center">
                <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse shrink-0" />
                <span className="text-[10px] font-black uppercase tracking-widest text-amber-600 whitespace-nowrap">
                  Ambiente de Demonstração
                </span>
              </div>
            ) : undefined
          }
        />
      </div>

      {/* Input Configurator */}
      <Card className="p-3 border-purple-200 bg-white/60 backdrop-blur-md shadow-xs">
        <div className="flex flex-col lg:flex-row gap-2">
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-50 rounded-xl border border-purple-200">
                <MessageSquare className="text-purple-600" size={18} />
              </div>
              <h3 className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                Fontes de Comentário
              </h3>
            </div>
            <Textarea
              value={commentsInput}
              onChange={(e) => setCommentsInput(e.target.value)}
              rows={4}
              className="bg-gray-50/50 border-purple-200 rounded-lg p-3 text-sm font-medium focus:ring-0 focus:border-purple-300 focus:bg-white transition-all resize-none shadow-xs"
              placeholder="Cole os comentários para análise (um por linha)..."
            />
          </div>
          <div className="lg:w-80 space-y-2 flex flex-col justify-end">
            <div className="space-y-2">
              <div className="flex items-center gap-3 mb-2 px-1">
                <Calendar className="text-purple-600" size={16} />
                <span className="text-[9px] font-black uppercase tracking-widest text-gray-400">
                  Rastreamento Técnico
                </span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={postId}
                  onChange={(e) => setPostId(e.target.value)}
                  className="w-20 h-11 shrink-0 bg-gray-50/50 border border-purple-200 rounded-lg px-2.5 text-sm font-bold focus:ring-0 focus:border-purple-300 transition-all shadow-xs"
                  placeholder="ID"
                  title="ID do Post (opcional)"
                />
                <Button
                  onClick={fetchSentimentData}
                  className="flex-1 min-w-0 h-11 font-black uppercase text-[10px] bg-purple-600 hover:bg-purple-700 shadow-xs rounded-lg transition-all whitespace-nowrap px-2!"
                >
                  Processar
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Spectrum Analysis Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {[
          {
            label: "Positivo",
            val: sentimentData?.sentiment_distribution?.positive,
            color: "emerald",
            icon: Smile,
          },
          {
            label: "Neutro",
            val: sentimentData?.sentiment_distribution?.neutral,
            color: "indigo",
            icon: Meh,
          },
          {
            label: "Negativo",
            val: sentimentData?.sentiment_distribution?.negative,
            color: "rose",
            icon: Frown,
          },
        ].map((s, i) => {
          const KPI_COLORS = {
            emerald: {
              border: "border-emerald-200",
              text: "text-emerald-700",
              iconBg: "bg-emerald-100",
              iconText: "text-emerald-600",
            },
            indigo: {
              border: "border-indigo-200",
              text: "text-indigo-700",
              iconBg: "bg-indigo-100",
              iconText: "text-indigo-600",
            },
            rose: {
              border: "border-rose-200",
              text: "text-rose-700",
              iconBg: "bg-rose-100",
              iconText: "text-rose-600",
            },
          };
          const c = KPI_COLORS[s.color as keyof typeof KPI_COLORS];

          return (
            <div
              key={i}
              className={cn(
                "relative bg-white rounded-xl border p-4 shadow-xs transition-all duration-200 overflow-hidden hover:-translate-y-0.5",
                c.border,
              )}
            >
              <div
                className={cn(
                  "absolute top-0 left-0 right-0 h-[3px]",
                  c.iconBg,
                )}
              />
              <div className="flex items-start justify-between pt-0.5">
                <div className="flex flex-col min-w-0">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 truncate">
                    {s.label}
                  </p>
                  <h3
                    className={cn(
                      "text-2xl font-black tracking-tight leading-none tabular-nums",
                      c.text,
                    )}
                  >
                    {s.val !== undefined ? `${s.val.toFixed(1)}%` : "N/D"}
                  </h3>
                </div>
                <div
                  className={cn(
                    "w-9 h-9 rounded-lg flex items-center justify-center shrink-0",
                    c.iconBg,
                    c.iconText,
                  )}
                >
                  <s.icon className="h-4 w-4" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Compound Visualizer */}
      <Card className="p-3 border-purple-200 bg-white/60 backdrop-blur-md">
        <SectionHeader
          icon={<BarChart3 className="h-5 w-5" />}
          title="Espectro de Afinidade Composta"
          subtitle="Distribuição percentual de sentimento na amostra"
          action={
            sentimentData && (
              <div className="px-4 py-1.5 bg-gray-50 border border-purple-200 rounded-full self-center">
                <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                  Total Analisado: {sentimentData.total_analyzed}
                </span>
              </div>
            )
          }
        />

        {sentimentData ? (
          <div className="h-16 w-full bg-gray-50/50 rounded-xl overflow-hidden flex p-1.5 border border-purple-200 shadow-inner">
            <div
              style={{
                width: `${sentimentData.sentiment_distribution.positive}%`,
              }}
              className="h-full bg-emerald-500 rounded-l-lg transition-all duration-1000 border-r-2 border-white/20"
            />
            <div
              style={{
                width: `${sentimentData.sentiment_distribution.neutral}%`,
              }}
              className="h-full bg-indigo-500 transition-all duration-1000 border-r-2 border-white/20"
            />
            <div
              style={{
                width: `${sentimentData.sentiment_distribution.negative}%`,
              }}
              className="h-full bg-rose-500 rounded-r-lg transition-all duration-1000"
            />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 py-8 px-4 text-center">
            <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
              <BarChart3 size={18} className="text-purple-300" />
            </div>
            <p className="text-xs font-black text-gray-700">
              Nenhuma análise ainda
            </p>
            <p className="text-[10px] font-semibold text-gray-400 max-w-[260px] leading-relaxed">
              Cole comentários acima e clique em "Processar" para ver a
              distribuição de sentimento.
            </p>
          </div>
        )}
      </Card>

      {/* Strategic Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        <Card className="p-3 border-purple-200 bg-linear-to-br from-white to-purple-50/30">
          <SectionHeader
            icon={<TrendingUp className="h-5 w-5" />}
            title="Diretrizes IA de Resposta"
            subtitle="Recomendações baseadas na análise de sentimento"
          />
          {sentimentData ? (
            <div className="space-y-2">
              {sentimentData.insights.map((insight, idx) => (
                <div
                  key={idx}
                  className="p-3 bg-white border border-purple-200 rounded-xl shadow-xs hover:border-purple-300 transition-colors"
                >
                  <div className="flex items-center gap-4 mb-3">
                    <div className="w-2.5 h-2.5 rounded-full bg-purple-600 animate-pulse shadow-lg shadow-purple-600/40" />
                    <h4 className="text-sm font-black text-gray-900 uppercase tracking-tight">
                      {insight.message}
                    </h4>
                  </div>
                  <p className="text-xs font-semibold text-gray-500 leading-relaxed italic border-l-2 border-purple-200 pl-4 ml-1">
                    "{insight.recommendation}"
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 py-8 px-4 text-center">
              <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                <TrendingUp size={18} className="text-purple-300" />
              </div>
              <p className="text-xs font-black text-gray-700">
                Nenhuma análise ainda
              </p>
              <p className="text-[10px] font-semibold text-gray-400 max-w-[260px] leading-relaxed">
                Cole comentários acima e clique em "Processar" para gerar
                diretrizes de resposta.
              </p>
            </div>
          )}
        </Card>

        <Card className="p-3 border-purple-200 bg-gray-50/30">
          <SectionHeader
            icon={<CheckCircle className="h-5 w-5" />}
            title="Boas Práticas Gerais"
            subtitle="Catalisadores e inibidores de resposta"
            iconBgClass="bg-emerald-500"
          />
          <p className="text-[10px] font-black uppercase tracking-widest text-amber-600 mb-2">
            Referência de conteúdo — não específico da sua conta
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <p className="text-[10px] font-black uppercase tracking-widest text-emerald-600 border-b border-emerald-100 pb-2">
                Catalisadores
              </p>
              <ul className="space-y-2">
                {[
                  "Vocabulário Inspiracional",
                  "Resolução de Dores",
                  "Suporte Humanizado",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    <span className="text-[11px] font-bold text-gray-600">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-2">
              <p className="text-[10px] font-black uppercase tracking-widest text-rose-600 border-b border-rose-100 pb-2">
                Inibidores
              </p>
              <ul className="space-y-2">
                {[
                  "Linguagem Abstrata",
                  "Crítica não Construtiva",
                  "Gatilhos Urgentes",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                    <span className="text-[11px] font-bold text-gray-600">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SentimentAnalysis;
